import React, { useState, useEffect } from 'react';
import { signUp, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingScreen from './LoadingScreen';
import {
  Box,
  Button,
  Container,
  Form,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Alert,
  Grid,
  Link
} from '@cloudscape-design/components';

function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState('register'); // 'register' or 'verify'
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if user came from login with unverified account
    if (location.state?.email && location.state?.needsVerification) {
      setEmail(location.state.email);
      setStep('verify');
    }
  }, [location.state]);

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  async function handleRegister(e) {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validation
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email: email,
            name: name,
          }
        }
      });
      setStep('verify');
    } catch (err) {
      console.error('Registration error:', err);

      // Provide user-friendly error messages
      let errorMessage = err.message;

      if (err.name === 'UsernameExistsException') {
        errorMessage = 'An account with this email already exists. Please try signing in instead.';
      } else if (err.name === 'InvalidPasswordException') {
        errorMessage = 'Password does not meet the requirements. Please ensure it has at least 8 characters with uppercase, lowercase, number, and special character.';
      } else if (err.name === 'InvalidParameterException') {
        errorMessage = 'Please check that your email address is valid and password meets the requirements.';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerification(e) {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setIsLoading(true);

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: verificationCode
      });
      navigate('/login', {
        state: {
          message: location.state?.needsVerification
            ? 'Account verified successfully! You can now sign in.'
            : 'Registration successful! Please sign in with your credentials.'
        }
      });
    } catch (err) {
      console.error('Verification error:', err);

      // Provide user-friendly error messages
      let errorMessage = err.message;

      if (err.name === 'CodeMismatchException') {
        errorMessage = 'Invalid verification code. Please check the code and try again.';
      } else if (err.name === 'ExpiredCodeException') {
        errorMessage = 'Verification code has expired. Please request a new code.';
      } else if (err.name === 'NotAuthorizedException') {
        errorMessage = 'Invalid verification code. Please check the code and try again.';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResendCode() {
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      await resendSignUpCode({ username: email });
      setSuccessMessage('Verification code sent! Please check your email.');
    } catch (err) {
      console.error('Resend code error:', err);
      setError(err.message || 'Failed to resend verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return <LoadingScreen message={step === 'register' ? 'Creating your account...' : 'Verifying your email...'} />;
  }

  return (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: "20px" }}>
      <Container>
        <SpaceBetween size="l">
          <Header
            variant="h1"
            description={
              step === 'register'
                ? 'Create your Recapify account'
                : location.state?.needsVerification
                  ? 'Complete your account verification'
                  : 'Check your email for verification code'
            }
            textAlign="center"
          >
            {step === 'register' ? 'Create Account' : 'Verify Email'}
          </Header>

          {successMessage && (
            <Alert type="success" header="Email Sent">
              {successMessage}
            </Alert>
          )}

          {error && (
            <Alert type="error" header="Error">
              {error}
            </Alert>
          )}

          {step === 'register' ? (
            <Form
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => navigate('/login')}>
                    Back to Sign In
                  </Button>
                  <Button variant="primary" onClick={handleRegister}>
                    Create Account
                  </Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="l">
                <FormField
                  label="Name"
                  description="Required - used as your display name"
                >
                  <Input
                    type="text"
                    value={name}
                    onChange={({ detail }) => setName(detail.value)}
                    placeholder="Enter your name"
                  />
                </FormField>

                <FormField
                  label="Email"
                  description="We'll send a verification code to this email"
                >
                  <Input
                    type="email"
                    value={email}
                    onChange={({ detail }) => setEmail(detail.value)}
                    placeholder="your.email@example.com"
                  />
                </FormField>

                <FormField
                  label="Password"
                  description="Must be at least 8 characters with uppercase, lowercase, number, and special character"
                >
                  <Input
                    type="password"
                    value={password}
                    onChange={({ detail }) => setPassword(detail.value)}
                  />
                </FormField>

                <FormField
                  label="Confirm Password"
                  description="Re-enter your password"
                >
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={({ detail }) => setConfirmPassword(detail.value)}
                  />
                </FormField>
              </SpaceBetween>
            </Form>
          ) : (
            <Form
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {location.state?.needsVerification && (
                    <Button variant="link" onClick={() => navigate('/login')}>
                      Back to Login
                    </Button>
                  )}
                  <Button variant="link" onClick={handleResendCode}>
                    Resend Code
                  </Button>
                  <Button variant="primary" onClick={handleVerification}>
                    Verify Email
                  </Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="l">
                <Alert type="info" header="Check your email">
                  {location.state?.needsVerification ? (
                    <>
                      Your account exists but needs verification. We've sent a verification code to <strong>{email}</strong>.
                      Please check your email and enter the code below to complete your registration.
                    </>
                  ) : (
                    <>
                      We've sent a verification code to <strong>{email}</strong>.
                      Please check your email and enter the code below.
                    </>
                  )}
                </Alert>

                {location.state?.needsVerification && (
                  <FormField
                    label="Email"
                    description="Account email (cannot be changed)"
                  >
                    <Input
                      type="email"
                      value={email}
                      disabled={true}
                      readOnly={true}
                    />
                  </FormField>
                )}

                <FormField
                  label="Verification Code"
                  description="Enter the 6-digit code from your email"
                >
                  <Input
                    type="text"
                    value={verificationCode}
                    onChange={({ detail }) => setVerificationCode(detail.value)}
                    placeholder="123456"
                  />
                </FormField>
              </SpaceBetween>
            </Form>
          )}
        </SpaceBetween>
      </Container>
    </div>
  );
}

export default Register;
